import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import axios from 'axios'
import { API_CONFIG, loadAppConfig } from '../../config/apiConfig'
import { showToast } from "../../utils/messageUtil"
import { parseLocalDateTime } from '../../utils/dateUtil'
import { useUser } from '../../contexts/UserContext'
import './index.css'

const getStatusText = (status) => {
    switch (status) {
        case 0: return '上级审批通过'
        case 1: return '待还车'
        case 2: return '用车审批驳回'
        case 3: return '还车待确认'
        case 4: return '已结束'
        case 5: return '还车不规范'
        case 6: return '取消用车'
        case 7: return '待上级审批'
        case 8: return '已撤销申请'
        case 9: return '上级审批驳回'
        default: return '未知状态'
    }
}

const getStatusClass = (status) => {
    switch (status) {
        case 1: case 7: return 'status-pending'
        case 0: return 'status-approved'
        case 8: case 5: case 9: return 'status-cancelled'
        case 4: case 6: case 2: return 'status-completed'
        default: return ''
    }
}

export default function PrivateCarRecord() {
    const navigate = useNavigate()
    const { userInfo, loading: userLoading, isNewFeatureExperience, checkingNewFeatureExperience, checkNewFeatureExperienceStatus } = useUser()
    const [records, setRecords] = useState([])
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [searchKeyword, setSearchKeyword] = useState('')
    const [actionLoading, setActionLoading] = useState({})
    const containerRef = useRef(null)

    // 权限守卫
    useEffect(() => {
        if (userLoading) return
        const uid = userInfo?.open_id || userInfo?.user_id
        if (uid) checkNewFeatureExperienceStatus(uid)
    }, [userLoading, userInfo, checkNewFeatureExperienceStatus])

    // 【2026-08-22 开放所有用户】移除新功能体验名单守卫（功能全量开放，不再弹回首页）
    /* useEffect(() => {
        if (userLoading || checkingNewFeatureExperience) return
        if (!isNewFeatureExperience) {
            showToast('您不在新功能体验名单中，不能访问', 'error')
            navigate('/', { replace: true })
        }
    }, [userLoading, checkingNewFeatureExperience, isNewFeatureExperience, navigate]) */

    // 获取我的私车记录（申请类型=1，只查申请人名下）
    const fetchRecords = async (page = 1) => {
        try {
            if (page === 1) setLoading(true); else setLoadingMore(true)
            const uid = userInfo?.open_id || userInfo?.user_id
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getMyPrivateCarApplicationList`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicantOpenId: uid,
                applicationType: 1,  // 只查私车申请
                page: page,
                rows: 10,
                keyword: searchKeyword || undefined
            }, {
                headers: { 'Content-Type': 'application/json' }
            })

            if (response.data && response.data.success) {
                const data = response.data.result.rows || []
                const records = response.data.result.records || 0
                const rowsPerPage = 10
                const calculatedTotalPages = Math.ceil(records / rowsPerPage)
                setTotalPages(calculatedTotalPages)
                setCurrentPage(page)
                setHasMore(page < calculatedTotalPages)
                if (page === 1) {
                    setRecords(data)
                } else {
                    setRecords(prev => [...prev, ...data])
                }
            } else {
                if (page === 1) setRecords([])
            }
        } catch (error) {
            console.error('获取私车记录失败:', error)
            if (page === 1) setRecords([])
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }

    // 初始化
    useEffect(() => {
        (async () => {
            await loadAppConfig();
            fetchRecords(1);
        })();
    }, [userInfo?.open_id, searchKeyword])

    // 加载更多
    const handleLoadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            fetchRecords(currentPage + 1)
        }
    }, [loadingMore, hasMore, currentPage])

    useEffect(() => {
        const handleScroll = () => {
            const container = containerRef.current
            if (!container || loadingMore || !hasMore) return
            const { scrollTop, scrollHeight, clientHeight } = container
            if (scrollHeight - scrollTop - clientHeight < 50) handleLoadMore()
        }
        const container = containerRef.current
        if (container) container.addEventListener('scroll', handleScroll)
        return () => { if (container) container.removeEventListener('scroll', handleScroll) }
    }, [loadingMore, hasMore, handleLoadMore])

    const handleSearch = () => fetchRecords(1)
    const handleResetSearch = () => setSearchKeyword('')

    // 私车记录操作（撤销申请/取消出差/结束出差）通用提交：成功后强制刷新列表
    const performAction = async (record, url, successMsg, errorPrefix) => {
        const applicationId = record.applicationId
        if (actionLoading[applicationId]) return
        setActionLoading(prev => ({ ...prev, [applicationId]: true }))
        try {
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}${url}`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicationId: applicationId
            }, {
                headers: { 'Content-Type': 'application/json' }
            })
            if (response.data && response.data.code === 200) {
                showToast(successMsg, 'success')
                // 成功后强制刷新列表（回到第一页）
                fetchRecords(1)
            } else {
                showToast(`${errorPrefix}:${response.data?.message || '未知错误'}`, 'error')
            }
        } catch (error) {
            console.error(`${errorPrefix}:`, error)
            showToast('网络错误，请稍后重试', 'error')
        } finally {
            setActionLoading(prev => ({ ...prev, [applicationId]: false }))
        }
    }

    // 撤销申请（撤销飞书审批实例）
    const handleCancelApplication = (record) =>
        performAction(record, '/carApplication/cancelPrivateCarApplication', '撤销申请成功', '撤销申请失败')

    // 取消出差（行程未开始，直接取消）
    const handleCancelTrip = (record) =>
        performAction(record, '/carApplication/cancelPrivateCarTrip', '取消出差成功', '取消出差失败')

    // 结束出差（行程已开始，计算里程金额）
    const handleFinishTrip = (record) =>
        performAction(record, '/carApplication/finishPrivateCarTrip', '结束出差成功', '结束出差失败')

    // 车辆轨迹：获取 18gps 轨迹回放 URL，成功后直接新窗口打开（飞书 WebView 内禁止内嵌 iframe，改为 window.open）
    const handlePlayback = async (record) => {
        const applicationId = record.applicationId
        if (actionLoading[applicationId]) return
        setActionLoading(prev => ({ ...prev, [applicationId]: true }))
        try {
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getPrivateCarPlaybackUrl`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicationId: applicationId
            }, {
                headers: { 'Content-Type': 'application/json' }
            })
            if (response.data && response.data.code === 200 && response.data.data) {
                window.open(response.data.data, '_blank')
                showToast('已在浏览器打开回放页', 'success')
            } else {
                showToast(response.data?.message || '获取轨迹失败', 'error')
            }
        } catch (error) {
            console.error('获取车辆轨迹失败:', error)
            showToast('网络错误，请稍后重试', 'error')
        } finally {
            setActionLoading(prev => ({ ...prev, [applicationId]: false }))
        }
    }

    return (
        <div className="private-car-record-page" ref={containerRef}>
            <div className="pcr-header">
                <h2>私车记录</h2>
            </div>

            {/* 搜索框 */}
            <div className="pcr-search-bar">
                <input
                    className="pcr-search-input"
                    placeholder="搜索车牌号、申请单号、imei"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
                />
                <button className="pcr-search-btn" onClick={handleSearch}>搜索</button>
                {searchKeyword && <button className="pcr-reset-btn" onClick={handleResetSearch}>重置</button>}
            </div>

            {loading ? (
                <div className="pcr-loading"><p>正在加载...</p></div>
            ) : records.length === 0 ? (
                <div className="pcr-empty"><p>暂无私车记录</p></div>
            ) : (
                <div className="pcr-list">
                    {records.map(record => {
                        // 用公共解析函数处理 "yyyy-MM-dd HH:mm:ss"（iOS 下 new Date 无效）
                        const startDate = parseLocalDateTime(record.expectedStartTime)
                        const now = Date.now()
                        const tripNotStarted = startDate && startDate.getTime() > now
                        const tripStarted = startDate && startDate.getTime() <= now
                        const status7 = record.approvalStatus === 7
                        const status1 = record.approvalStatus === 0
                        const isLoading = !!actionLoading[record.applicationId]
                        return (
                            <div key={record.applicationId} className="pcr-item">
                                <div className="pcr-item-header">
                                    <span className="pcr-plate">{record.plateNumber || '-'}</span>
                                    <div className="pcr-header-right">
                                        <span className={`pcr-status ${getStatusClass(record.approvalStatus)}`}>
                                            {getStatusText(record.approvalStatus)}
                                        </span>
                                        {status7 && (
                                            <button
                                                className="pcr-action-btn pcr-btn-cancel-apply"
                                                onClick={() => handleCancelApplication(record)}
                                                disabled={isLoading}
                                            >撤销申请</button>
                                        )}
                                        {status1 && tripNotStarted && (
                                            <button
                                                className="pcr-action-btn pcr-btn-cancel-trip"
                                                onClick={() => handleCancelTrip(record)}
                                                disabled={isLoading}
                                            >取消出差</button>
                                        )}
                                        {status1 && tripStarted && (
                                            <button
                                                className="pcr-action-btn pcr-btn-finish-trip"
                                                onClick={() => handleFinishTrip(record)}
                                                disabled={isLoading}
                                            >结束出差</button>
                                        )}
                                        {record.approvalStatus === 4 && (
                                            <button
                                                className="pcr-action-btn pcr-btn-playback"
                                                onClick={() => handlePlayback(record)}
                                                disabled={isLoading}
                                            >车辆轨迹</button>
                                        )}
                                    </div>
                                </div>
                                <div className="pcr-details">
                                    <div className="pcr-detail-item">
                                        <span className="pcr-label">申请人:</span>
                                        <span className="pcr-value">{record.applicantName}</span>
                                    </div>
                                    <div className="pcr-detail-item">
                                        <span className="pcr-label">手机号:</span>
                                        <span className="pcr-value">{record.applicantPhone}</span>
                                    </div>
                                    <div className="pcr-detail-item">
                                        <span className="pcr-label">申请单号:</span>
                                        <span className="pcr-value">{record.applicationNo}</span>
                                    </div>
                                    <div className="pcr-detail-item">
                                        <span className="pcr-label">申请时间:</span>
                                        <span className="pcr-value">{record.applyTime}</span>
                                    </div>
                                    <div className="pcr-detail-item full-width">
                                        <span className="pcr-label">用车时间:</span>
                                        <span className="pcr-value">{record.expectedStartTime} 至 {record.expectedEndTime}</span>
                                    </div>
                                    <div className="pcr-detail-item">
                                        <span className="pcr-label">出发地</span>
                                        <span className="pcr-value">{record.expectedLocation || '-'}</span>
                                    </div>
                                    <div className="pcr-detail-item">
                                        <span className="pcr-label">目的地:</span>
                                        <span className="pcr-value">{record.destination || '-'}</span>
                                    </div>
                                    <div className="pcr-detail-item">
                                        <span className="pcr-label">行驶里程:</span>
                                        <span className="pcr-value">{record.totalMileage != null ? `${record.totalMileage} km` : '-'}</span>
                                    </div>
                                    <div className="pcr-detail-item">
                                        <span className="pcr-label">用车费用:</span>
                                        <span className="pcr-value">{record.amount != null ? `${record.amount} 元` : '-'}</span>
                                    </div>
                                    {record.passengers && record.passengers.trim() !== '' && (
                                        <div className="pcr-detail-item">
                                            <span className="pcr-label">同行人:</span>
                                            <span className="pcr-value">{record.passengers}</span>
                                        </div>
                                    )}
                                    <div className="pcr-detail-item">
                                        <span className="pcr-label">设备编号:</span>
                                        <span className="pcr-value">{record.imei || '-'}</span>
                                    </div>
                                    <div className="pcr-detail-item full-width">
                                        <span className="pcr-label">用车原因:</span>
                                        <span className="pcr-value">{record.reason || '-'}</span>
                                    </div>

                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {loadingMore ? (
                <div className="pcr-loading-more"><p>加载中...</p></div>
            ) : hasMore ? (
                <div className="pcr-load-more">
                    <button className="load-more-btn" onClick={handleLoadMore}>
                        加载更多
                    </button>
                </div>
            ) : (
                records.length > 0 && <div className="pcr-no-more"><p>没有更多了</p></div>
            )}

            {/* PC端返回首页按钮（移动端隐藏） */}
            <div className="pcr-back-home">
                <button className="back-btn" onClick={() => navigate('/')}>返回首页</button>
            </div>

        </div>
    )
}
